import React from 'react';
import classNames from 'clsx';
import { useService } from '@wix/services-manager-react';
import type { IUrlService } from '@wix/viewer-service-url/definition';
import { UrlDefinition } from '@wix/viewer-service-url/definition';
import type { IAnchorsService } from '@wix/viewer-service-anchors/definition';
import { AnchorsDefinition } from '@wix/viewer-service-anchors/definition';
import type { ITranslationsService } from '@wix/viewer-service-translations/definition';
import { TranslationsDefinition } from '@wix/viewer-service-translations/definition';
import {
  ARIA_LABEL_NAMESPACE,
  translationsKeys,
  defaultTranslations,
} from './constants';
import { MenuContextProvider } from './contexts/MenuContext';
import { MenuAnimationName, selectors } from './constants';
import type { MenuProps as MenuBaseProps, MenuStates } from './Menu.types';

export type MenuProps = MenuBaseProps & MenuStates;
import animationClasses from './styles/animations.module.scss';
import presetWrapperStyles from '../../../common-styles/presetWrapper.module.scss';
import { NavbarMenu } from './components/Navbar';
import { HamburgerMenu } from './components/Hamburger';
import { convertA11yKeysToHtmlFormat } from '../../../utils/a11y';

const Menu: React.FC<MenuProps> = props => {
  const {
    id,
    className,
    elementProps: {
      navbar: navbarProps = {
        items: [],
        a11y: { ariaLabel: '' },
      },
    },
    hamburgerMenuOpen,
    wix,
    animationName = MenuAnimationName.Calm,
    onMouseIn,
    onMouseOut,
    onItemClick,
    onItemDblClick,
    onItemMouseIn,
    onItemMouseOut,
  } = props;

  const { getCurrentUrl } = useService(UrlDefinition) as IUrlService;
  const currentUrl = getCurrentUrl();
  const { getActiveAnchor } = useService(AnchorsDefinition) as IAnchorsService;
  const activeAnchor = getActiveAnchor();

  const { translate } = useService(
    TranslationsDefinition,
  ) as ITranslationsService;
  const translations = {
    menuNavAriaLabel: translate(ARIA_LABEL_NAMESPACE)(
      translationsKeys.menuNavAriaLabel,
      defaultTranslations.menuNavAriaLabel,
    ),
    dropdownButtonAriaLabel: translate(ARIA_LABEL_NAMESPACE)(
      translationsKeys.dropdownButtonAriaLabel,
      defaultTranslations.dropdownButtonAriaLabel,
    ),
    hamburgerOpenButtonAriaLabel: translate(ARIA_LABEL_NAMESPACE)(
      translationsKeys.hamburgerOpenButtonAriaLabel,
      defaultTranslations.hamburgerOpenButtonAriaLabel,
    ),
    hamburgerCloseButtonAriaLabel: translate(ARIA_LABEL_NAMESPACE)(
      translationsKeys.hamburgerCloseButtonAriaLabel,
      defaultTranslations.hamburgerCloseButtonAriaLabel,
    ),
  };

  const presetsWrapperProps = wix?.presetsWrapperProps || {};

  return (
    <div className={presetWrapperStyles.presetWrapper} {...presetsWrapperProps}>
      <div
        id={id}
        className={classNames(
          className,
          selectors.menu,
          animationClasses[`animation-${animationName}`],
        )}
        onMouseEnter={onMouseIn}
        onMouseLeave={onMouseOut}
      >
        <MenuContextProvider
          items={navbarProps.items || []}
          translations={translations}
          getAnimationPackage={() => animationName}
          currentUrl={currentUrl}
          activeAnchor={activeAnchor}
          navAriaLabel={navbarProps?.a11y?.ariaLabel ?? ''}
          a11yProps={convertA11yKeysToHtmlFormat(navbarProps?.a11y ?? {})}
        >
          <NavbarMenu
            id={id}
            {...navbarProps}
            onItemClick={onItemClick}
            onItemDblClick={onItemDblClick}
            onItemMouseIn={onItemMouseIn}
            onItemMouseOut={onItemMouseOut}
          />
          <HamburgerMenu
            {...props}
            animationName={animationName}
            hamburgerMenuOpen={hamburgerMenuOpen}
          />
        </MenuContextProvider>
      </div>
    </div>
  );
};

export default Menu;
